import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import tw from "tailwind-styled-components";
import autoAnimate from "@formkit/auto-animate";
import FlagIcon from "@mui/icons-material/Flag";
import PlaceIcon from "@mui/icons-material/Place";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Header, SearchInput, Text, Button, HeadingText } from "@components";
import { Storage, updateRecentData, imageUrlBuilder } from "@utils";
import { updateCustomDate } from "@redux/header/slice";
import { upsertRecentSearches } from "@redux/user/slice";
import { clubSearchApiList } from "@redux/teetime/actions";
import { toast } from "react-toastify";
import bg from "@assets/images/bg.png";
import countryCode from "./../../countryCode/index.json";
import SkeletonGroup from "../customSkeleton";

const Carousel = ({ images, isCompact, data }) => {
  const navigate = useNavigate();
  const carouselRef = useRef();
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalImages = data?.length + (images?.length ? 1 : 0);

  useEffect(() => {
    let intervalId = setInterval(() => {
      nextSlide();
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [currentIndex, totalImages]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalImages);
  }, [totalImages]);

  const setSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div
      className={`relative w-full lg:h-[667px] md:h-[500px] h-[400px] max-h-[667px]
       overflow-hidden`}
    >
      <div
        ref={carouselRef}
        className="flex w-full h-full transition-transform duration-1000 ease-in-out"
        style={{
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {images?.length ? (
          <div className="w-full h-full flex-shrink-0 min-w-full relative">
            <img
              src={images[0]?.url}
              alt=""
              className="w-full h-full object-cover animate-scaleLoop"
            />
          </div>
        ) : null}

        {data?.length ? (
          data?.map((image, index) => (
            <div
              key={image?.id || index}
              className="w-full h-full flex-shrink-0 min-w-full relative "
            >
              <img
                src={image?.photos || image?.photos?.[0] || bg}
                onError={(e) => (e.target.src = image?.photos?.[0] || bg)}
                className={`w-full h-full object-cover ${
                  index === currentIndex ? "" : "animate-scaleLoop"
                }`}
                alt=""
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blackText/60 to-transparent" />

              <div className="absolute md:bottom-10 bottom-2 left-4 md:left-10 xl:left-20 h-[150px] flex flex-col items-start justify-start gap-y-3 w-full pr-20">
                <div className="md:w-[90%] w-full">
                  <div className="w-full h-[1px] mb-2 opacity-80 bg-buttonTextColor" />
                  {image?.address && (
                    <p className="lg:text-[16px] text-[15px] text-buttonTextColor font-semibold text-start pt-1">
                      {`${
                        image?.address?.place
                          ? image?.address?.place + ", "
                          : ""
                      } ${
                        image?.address?.region ? image?.address?.region : ""
                      } ${
                        image?.address?.state
                          ? image?.address?.state + ", "
                          : ""
                      } ${
                        image?.address?.zipcode
                          ? image?.address?.zipcode + ", "
                          : ""
                      } ${
                        image?.address?.country ? image?.address?.country : ""
                      }`}
                    </p>
                  )}
                  <div className="w-full h-auto flex flex-row items-center justify-between">
                    <div className="w-auto h-auto text-start">
                      <HeadingText
                        className={
                          "text-[18px] md2:text-[22px] text-buttonTextColor font-bold"
                        }
                      >
                        {image?.name}
                      </HeadingText>
                    </div>
                    <button
                      onClick={() =>
                        navigate(`/course/${image?.name.toString()}`)
                      }
                      className="w-[170px] px-4 h-[45px] border border-buttonTextColor text-buttonTextColor hover:bg-buttonTextColor hover:text-buttonColor rounded-full bg-transparent md:flex hidden items-center justify-center"
                    >
                      <p className="text-[17px] font-medium">
                        {"More Details"}
                      </p>
                    </button>
                  </div>
                  <div className="md:w-[75%] w-full h-auto line-clamp-2">
                    <p className="text-[16px] text-start text-buttonTextColor opacity-80">
                      {image?.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <></>
        )}
      </div>

      <div className="h-full absolute bottom-0 top-0 right-6 flex flex-col items-center justify-center">
        <div className="h-fit  flex flex-col gap-y-4 z-10">
          {(images?.length ? ["", ...data] : data)?.map((_, index) => (
            <button
              key={index}
              onClick={() => setSlide(index)}
              className={`w-7 h-7 rounded-full border-[1px] flex justify-center items-center ${
                index === currentIndex
                  ? "border-[#ffffff]"
                  : "border-transparent"
              }`}
            >
              <div className="w-1 h-1 rounded-full bg-[#ffffff]" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const HeaderSection = ({
  headerImage,
  isCompact = true,
  searchInputProps,
  searchMenuProps,
  isMapSearchLoader,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { recentSearches } = useSelector((store) => store.userReducer);
  const { logo, customDate, ismobileMenuOpen } = useSelector(
    (state) => state.headerReducer
  );
  const { exploreGame, clubSearchList } = useSelector(
    (store) => store.teetimeReducer
  );
  const headerBgImages = logo?.data?.bgImage;
  const headerClubsData = logo?.data?.clubs;
  const announcementTxt = logo?.data?.announcementText;
  const timeoutRef = useRef();
  const courseRef = useRef();
  const locationRef = useRef();
  let homeBgImg = "";
  let courseBgImg = "";

  setTimeout(() => {
    homeBgImg = logo?.data && logo?.data?.bgImage[0]?.url;

    courseBgImg = logo?.data && logo?.data?.bgImage[1]?.url;
  }, [2000]);

  const [state, setState] = useState({
    search: "",
    show: false,
    courseList: true,
    locationList: true,
    announcementText: "",
    isSearchLoader: false,
  });

  const changeDate = (e) => dispatch(updateCustomDate({ date: e }));

  const showSuggestionBox = (e) => {
    e.stopPropagation();
    !state.show && setState((prev) => ({ ...prev, show: true }));
  };
  const hideSuggestionBox = () =>
    state.show && setState((prev) => ({ ...prev, show: false }));

  const setSearchData = (e) => {
    let payload = `${e.target.value}`;
    setState((prev) => ({ ...prev, search: payload, isSearchLoader: true }));
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      dispatch(clubSearchApiList(payload)).then((res) => {
        setState((prev) => ({ ...prev, isSearchLoader: false }));
      });
    }, 1000);
  };

  useEffect(() => {
    dispatch(clubSearchApiList(""));
  }, []);

  const updateRecentSearches = async (e) => {
    let recentSearchesData = [...recentSearches];
    updateRecentData(recentSearchesData, e);
    dispatch(upsertRecentSearches(recentSearchesData));
    Storage.save(Storage.RECENT_SEARCHES, JSON.stringify(recentSearchesData));
  };

  const removeRecentSearchs = async () => {
    dispatch(upsertRecentSearches([]));
    await Storage.remove(Storage.RECENT_SEARCHES);
  };

  const onSearch = (e) => {
    hideSuggestionBox();
    let query = `?date=${moment(customDate).toISOString()}`;
    if (e === "text") {
      if (state.search.length) {
        if (clubSearchList?.location?.length) {
          if (
            clubSearchList?.location?.every((item) =>
              item?.country?.toLowerCase()?.includes(state.search.toLowerCase())
            )
          ) {
            query = query + "&country=" + clubSearchList?.location[0]?.country;
          } else {
            // if (clubSearchList?.location[0]?.place?.toLowerCase().includes(state.search.toLowerCase()) || clubSearchList?.location[0]?.country?.toLowerCase().includes(state.search.toLowerCase())) {
            query =
              query +
              "&place=" +
              clubSearchList?.location[0]?.place +
              "&country=" +
              clubSearchList?.location[0]?.country;
          }
        } else if (clubSearchList?.club?.length) {
          query = query + "&name=" + clubSearchList?.club[0]?.club;
        }
      }
      window.location.search !== query &&
        navigate({
          pathname: `/courses`,
          search: query,
        });
    } else {
      if (!e) {
        toast.error(t("header.retry_Search"));
        return;
      }
      if (e?.club) {
        query = query + "&name=" + e.club;
      }
      if (e?.place) {
        query = query + "&place=" + e.place;
      }
      if (e?.country) {
        query = query + "&country=" + e.country;
      }
      if (e?.slug) {
        query = query + "&slug=" + e.slug;
      } else if (e) {
        updateRecentSearches(e);
      }

      if (e?.club) {
        navigate(`/course/${e?.club.toString()}`);
      } else {
        navigate({
          pathname: `/courses`,
          search: query,
        });
      }
    }
  };

  const onClickBook = (e) => {
    hideSuggestionBox();
    let data = { clubId: e.id };
    navigate("/book", { state: data });
  };

  useEffect(() => {
    if (courseRef.current) {
      autoAnimate(courseRef.current);
    }
  }, [courseRef]);

  const showMoreCourse = (e) => {
    e.stopPropagation();
    setState((prev) => ({
      ...prev,
      courseList: !state.courseList,
    }));
  };

  useEffect(() => {
    if (locationRef.current) {
      autoAnimate(locationRef.current);
    }
  }, [locationRef]);

  const showMoreLocation = (e) => {
    e.stopPropagation();
    setState((prev) => ({
      ...prev,
      courseList: false,
      locationList: !state.locationList,
    }));
  };

  const ClubsListView = ({ club }) => (
    <div className="w-full h-auto" ref={courseRef}>
      <div
        onClick={showMoreCourse}
        className={`w-full flex justify-between items-center cursor-pointer bg-white pl-2 ${
          state.courseList ? "rounded-t-lg" : "rounded-lg"
        }`}
      >
        <p className="text-[20px] text-blackText font-sans font-semibold">
          {t("header.courses")}
        </p>
        <div
          className={`w-fit h-fit ${
            state.courseList
              ? "duration-500 transform rotate-90"
              : "duration-300 transform rotate-0"
          }`}
        >
          <ArrowRightIcon fontSize="large" sx={{ color: "#000000" }} />
        </div>
      </div>
      {state.courseList && (
        <div className="flex flex-col bg-[#ffffffad] max-h-[220px] overflow-y-scroll custom-scroll rounded-b-lg">
          {club.map((ele, ind) => (
            <div
              key={ind}
              className="mt-1 w-full py-1 flex justify-between items-center"
            >
              <div className="flex">
                <FlagIcon
                  className="text-buttonColor self-center cursor-pointer"
                  onClick={() => onSearch(ele)}
                />
                <div
                  className="lg:w-[310px] md2:w-[240px] w-[150px] truncate "
                  onClick={() => onSearch(ele)}
                >
                  <Text
                    className={`${SearchSuggestionText} truncate cursor-pointer`}
                  >
                    {ele?.club}
                  </Text>
                </div>
              </div>
              <div className="flex gap-x-2 items-center">
                <div
                  onClick={() => onSearch(ele)}
                  className="md:w-[70px] w-[60px] py-1 flex items-center justify-center bg-buttonColor rounded-md hover:opacity-70 cursor-pointer"
                >
                  <p className="text-buttonTextColor">Show</p>
                </div>
                <div
                  onClick={() => onClickBook(ele)}
                  className="md:w-[70px] w-[60px] py-1 flex items-center justify-center bg-buttonColor rounded-md hover:opacity-70 cursor-pointer"
                >
                  <p className="text-buttonTextColor">
                    {t("destination.book_Now")}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <hr className="border-buttonColor my-2" />
    </div>
  );

  const LocationListView = ({ location }) => (
    <div className="w-full h-auto" ref={locationRef}>
      <div
        onClick={showMoreLocation}
        className={`w-full flex justify-between items-center cursor-pointer bg-white pl-2 ${
          state.locationList ? "rounded-t-lg" : "rounded-lg"
        }`}
      >
        <p className="text-[18px] font-sans text-blackText font-semibold">
          {t("header.locations")}
        </p>
        <div
          className={`w-fit h-fit ${
            state.locationList
              ? "duration-500 transform rotate-90"
              : "duration-300 transform rotate-0"
          }`}
        >
          <ArrowRightIcon fontSize="large" sx={{ color: "#000000" }} />
        </div>
      </div>
      {state.locationList && (
        <div className="flex flex-col bg-[#ffffffad] max-h-[200px] overflow-y-scroll custom-scroll rounded-b-lg">
          {location.every((it) => it.country === location[0].country) && (
            <Button
              key={-1}
              onClick={() => onSearch({ country: location[0].country })}
              variant="primary"
              className="mt-1"
            >
              <div className="py-2 flex">
                <PlaceIcon className="text-buttonColor self-center" />
                <img
                  src={`https://flagcdn.com/w40/${
                    countryCode[location[0].country.toLowerCase()]
                  }.png`}
                  srcSet={`https://flagcdn.com/w80/${
                    countryCode[location[0].country.toLowerCase()]
                  }.png 2x`}
                  alt="flag"
                  style={{
                    width: "1.3em",
                    height: "0.8em",
                    marginTop: "0.3em",
                    marginRight: "0.2em",
                  }}
                />
                <Text className={SearchSuggestionText}>
                  {location[0].country},{" "}
                </Text>
                <span className="font-sans font-thin italic text-blackText ml-1">
                  {`${location.length} golf club`}
                </span>
              </div>
            </Button>
          )}
          {location.map((ele, ind) => (
            <Button
              key={ind}
              onClick={() => onSearch(ele)}
              variant="primary"
              className="mt-1"
            >
              <div className="py-2 flex">
                <PlaceIcon className="text-buttonColor self-center" />
                <img
                  src={`https://flagcdn.com/w40/${
                    countryCode[ele.country.toLowerCase()]
                  }.png`}
                  srcSet={`https://flagcdn.com/w80/${
                    countryCode[ele.country.toLowerCase()]
                  }.png 2x`}
                  alt="flag"
                  style={{
                    width: "1.3em",
                    height: "0.8em",
                    marginTop: "0.3em",
                    marginRight: "0.2em",
                  }}
                />
                <Text
                  className={SearchSuggestionText}
                >{`${ele.place}, ${ele.country}`}</Text>
              </div>
            </Button>
          ))}
        </div>
      )}
    </div>
  );

  const SearchMenu = ({
    suggestionSearch,
    recentSearch,
    clubSearchList,
    search,
    onSearch,
    isSearchLoader,
  }) => {
    const { club, location } = clubSearchList;

    return (
      <div className={SearchContainer} onClick={showSuggestionBox}>
        {isMapSearchLoader || isSearchLoader ? (
          <SkeletonGroup />
        ) : (club?.length || location?.length) && search?.length ? (
          <>
            {/* Club Suggestions */}
            {club?.length > 0 && <ClubsListView club={club} />}

            {/* Location Suggestions */}
            {location?.length > 0 && <LocationListView location={location} />}
          </>
        ) : search?.length ? (
          <>
            <p className="text-[18px] font-sans text-blackText font-semibold">
              {t("header.no_Results")}
            </p>
            <hr className="border-[#dddddddd] pb-5" />
          </>
        ) : recentSearch?.length > 0 ? (
          <>
            <div className="w-full flex flex-row items-center justify-between">
              <p className="text-[18px] font-sans text-blackText font-semibold">
                {t("header.recent_Search")}
              </p>
              <button
                onClick={removeRecentSearchs}
                className="text-[18px] font-sans text-primary hover:underline underline-offset-2 hover:opacity-70 font-semibold"
              >
                {"Clear recents"}
              </button>
            </div>
            <hr className="border-primary pb-2 mt-2" />
            <div className="flex flex-col">
              {recentSearch.map((ele, ind) => (
                <div
                  key={ind}
                  className="mt-1 flex justify-between items-center"
                >
                  <span className="py-2 flex">
                    {ele.type === "club" ? (
                      <FlagIcon
                        className="text-buttonColor cursor-pointer"
                        onClick={() => onSearch(ele)}
                      />
                    ) : (
                      <PlaceIcon className="text-buttonColor" />
                    )}
                    <div
                      className="md:w-[310px] sm:w-[240px] w-[150px] truncate cursor-pointer"
                      onClick={() => onSearch(ele)}
                    >
                      <Text className={`${SearchSuggestionText} truncate`}>
                        {ele.place
                          ? `${ele.place}, ${ele.country}`
                          : ele.club || ele.country}
                      </Text>
                    </div>
                  </span>
                  <div className="flex gap-x-2 items-center">
                    <div
                      onClick={() => onSearch(ele)}
                      className="md:w-[70px] w-[60px] py-1 flex items-center justify-center bg-buttonColor rounded-md hover:opacity-70 cursor-pointer"
                    >
                      <p className="text-buttonTextColor">Show</p>
                    </div>
                    {ele.type === "club" && (
                      <div
                        onClick={() => onClickBook(ele)}
                        className="md:w-[70px] w-[60px] py-1 flex items-center justify-center bg-buttonColor rounded-md hover:opacity-70 cursor-pointer"
                      >
                        <p className="text-buttonTextColor">
                          {t("destination.book_Now")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <hr className="border-[#dddddddd] pb-5" />
          </>
        ) : (
          <>
            {club?.length > 0 && <ClubsListView club={club} />}
            {location?.length > 0 && <LocationListView location={location} />}
          </>
        )}
      </div>
    );
  };

  return (
    <Container onClick={hideSuggestionBox} className="relative">
      <Header
        searchInputProps={searchInputProps}
        searchMenuProps={searchMenuProps}
      />
      <div
        className={`${RootHeader} relative flex justify-center items-center`}
      >
        {headerBgImages?.length ? (
          <Carousel
            data={headerClubsData}
            images={headerBgImages}
            isCompact={isCompact}
          />
        ) : null}
        <div className={`absolute lg:mt-[150px] mt-[0px]`}>
          <SearchInput
            isCompact={isCompact}
            value={state.search}
            selectedDate={customDate}
            setShow={showSuggestionBox}
            changeSelectedDate={changeDate}
            setSearch={setSearchData}
            goToSearch={onSearch}
            {...searchInputProps}
          />

          <div
            className={
              state.show
                ? "absolute z-[998] left-0 right-0 visible"
                : "absolute invisible h-0"
            }
          >
            {state.search ||
            recentSearches?.length ||
            searchInputProps?.value ? (
              <SearchMenu
                suggestionSearch={exploreGame?.data}
                recentSearch={recentSearches}
                clubSearchList={clubSearchList}
                search={state.search}
                onSearch={onSearch}
                isSearchLoader={state.isSearchLoader}
                {...searchMenuProps}
              />
            ) : (
              <SearchMenu
                clubSearchList={clubSearchList}
                search={state.search}
                onSearch={onSearch}
                isSearchLoader={state.isSearchLoader}
                {...searchMenuProps}
              />
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

const Container = tw.div`
bg-white
`;

const RootHeader = `
w-full
lg:h-[667px] md:h-[500px] h-[400px]
max-h-[667px]


`;

const SearchSuggestionText = `
text-blackText
font-sans
font-semibold
hover:underline
underline-offset-2
text-start
`;

const RootHeader2 = `
bg-no-repeat
bg-cover
bg-center
w-full
h-[545px]
max-h-[545px]
bg-[linear-gradient(to_bottom,rgba(0,0,0,0.3)_0%,rgba(0,0,0,0)_33%),var(--image-url)]
`;

const Li = tw.div`
flex-1
flex 
p-2
justify-center
items-center
bg-white
flex
border-[#dddddddd] 
rounded-md 
self-center
`;

const SearchContainer = `
h-[300px] 
flex 
flex-col 
lgw-[90%] w-full 
border-[gray] 
bg-[#ffffffcc] 
rounded-lg 
shadow-[0_0px_20px_#3c7fed90]
mt-5 
lg:px-4 px-2
pt-2 
pb-5
text-white 
m-auto 
overflow-y-scroll 
overflow-x-hidden 
custom-scroll 
transparent `;

export default HeaderSection;
